import { useState } from 'react'
import { Globe2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SearchSelect } from '@/components/composites/search-select'
import { browserTimeZone, describeTimeZone, timeZoneOptions } from '@/lib/timezone'
import { cn } from '@/lib/utils'
import { usePreferences, useUpdatePreferences } from './usePreferences'

/**
 * Choose the timezone the whole app shows times in. "Follow my device" stores null, so a person
 * who travels keeps getting local times; picking a zone pins it on every device they sign in on.
 */
export function TimeZoneDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const prefs = usePreferences()
  const update = useUpdatePreferences()
  const saved = prefs.data?.timezone ?? null
  const device = browserTimeZone()
  const [mode, setMode] = useState<'device' | 'pick'>(saved ? 'pick' : 'device')
  const [zone, setZone] = useState(saved ?? device)
  const [options] = useState(timeZoneOptions)

  const save = () => {
    const next = mode === 'device' ? null : zone
    update.mutate({ timezone: next }, {
      onSuccess: () => {
        toast.success(`Times now show in ${describeTimeZone(next ?? device)}`)
        onOpenChange(false)
      },
      onError: () => toast.error('Could not save your timezone. Try again.'),
    })
  }

  const choice = (value: 'device' | 'pick', title: string, detail: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={mode === value}
      onClick={() => setMode(value)}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
        mode === value ? 'border-brand bg-brand-soft' : 'border-border hover:bg-surface-sunken',
      )}
    >
      <span className={cn('mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border', mode === value ? 'border-brand' : 'border-border-strong')}>
        {mode === value ? <span className="size-2 rounded-full bg-brand" /> : null}
      </span>
      <span>
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span className="block text-xs text-text-secondary">{detail}</span>
      </span>
    </button>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Globe2 className="size-5 text-brand" /> Your timezone</DialogTitle>
          <DialogDescription>Dates, times, report ranges and exports all show in this zone. Saved to your account.</DialogDescription>
        </DialogHeader>
        <div role="radiogroup" className="grid gap-2">
          {choice('device', 'Follow my device', describeTimeZone(device))}
          {choice('pick', 'Always use a specific timezone', 'Same times on every device you sign in on')}
        </div>
        {mode === 'pick' ? (
          <SearchSelect value={zone} onChange={setZone} options={options} placeholder="Search a city or zone…" stackHints />
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
